import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { wsClient } from '@/lib/wsClient';
import { useWsStatus } from '@/hooks/useWsStatus';
import { chime } from '@/lib/audio';
import { useUIStore } from '@/stores/uiStore';
import type { WSMessage } from '@/types/api';

export function useWebSocket(enabled: boolean) {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const soundOn = useUIStore((s) => s.soundOn);
  const status = useWsStatus();

  useEffect(() => {
    if (!enabled) {
      wsClient.disconnect();
      return;
    }
    wsClient.connect();

    const offReconnect = wsClient.onReconnect(() => {
      qc.invalidateQueries({ queryKey: ['orders'] });
    });
    const offMsg = wsClient.onMessage((msg: WSMessage) => {
      if (msg.type === 'order.created') {
        if (soundOn) chime();
        toast.success(t('orders.toasts.newOrder', { id: msg.payload.orderId }));
        qc.invalidateQueries({ queryKey: ['orders'] });
      } else if (msg.type === 'order.updated') {
        qc.invalidateQueries({ queryKey: ['orders'] });
        qc.invalidateQueries({ queryKey: ['order', msg.payload.orderId] });
      }
    });

    return () => {
      offReconnect();
      offMsg();
    };
  }, [enabled, qc, soundOn, t]);

  return status;
}
